import { IChatMessage } from '../../libs/models/ChatMessage';
import { ContentMetadata } from '../../libs/models/ContentMetadata';

interface Questions {
    aq: string[];
}

/**
 * One row in the deduplicated citation footer. Multiple backend `ContentMetadata` entries that
 * share the same URL collapse into a single row, and every inline `[[#n]]` that resolved to any
 * of them renders the same superscript number. Atomic results frequently produce this collision:
 * two atoms with different ParentIds list the same generic help-center article as their primary
 * source, and the user shouldn't see "2) Rapporter / 3) Rapporter" — same link twice.
 */
export interface CitationDisplayEntry {
    displayOrder: number;
    title?: string;
    url?: string;
}

export interface CitationView {
    /** Deduplicated rows, in the order they were first cited in the answer. */
    visibleEntries: CitationDisplayEntry[];
    /**
     * Maps backend `ContentMetadata.id` → the display number rendered in the inline `<sup>`.
     * Two ids that resolved to the same URL share a display number, which is why the inline
     * marker matches the footer entry.
     */
    displayOrderById: Map<number, number>;
}

export function computeCitationView(metadata: ContentMetadata[] | undefined): CitationView {
    if (!metadata || metadata.length === 0) {
        return { visibleEntries: [], displayOrderById: new Map() };
    }

    // Order-of-appearance ascending is the canonical reading order. Entries with
    // orderOfAppearance=0 were carried through metadata but never cited inline — drop them
    // from the visible list (current behavior) and from the dedup since there's no marker to
    // match.
    const inOrder = [...metadata]
        .filter((m) => m.orderOfAppearance > 0)
        .sort((a, b) => a.orderOfAppearance - b.orderOfAppearance);

    const keyToDisplayOrder = new Map<string, number>();
    const visibleEntries: CitationDisplayEntry[] = [];
    const displayOrderById = new Map<number, number>();

    for (const m of inOrder) {
        // Group by URL when present; fall back to title for entries with no URL (rare — the
        // backend produces null URLs only when the SDK couldn't parse one). Coerce empty
        // strings to undefined first so they fall through instead of becoming the dedup key.
        const url = m.url?.length ? m.url : undefined;
        const title = m.title?.length ? m.title : undefined;
        const key = url ?? title ?? `__id_${m.id.toString()}`;
        let displayOrder = keyToDisplayOrder.get(key);
        if (displayOrder === undefined) {
            displayOrder = visibleEntries.length + 1;
            keyToDisplayOrder.set(key, displayOrder);
            visibleEntries.push({ displayOrder, title: m.title, url: m.url });
        }
        displayOrderById.set(m.id, displayOrder);
    }

    return { visibleEntries, displayOrderById };
}

/**
 * Resolve the comma-separated `ContentMetadata` ids carried by an inline `<sup data-ref-ids>`
 * marker to the deduplicated citation entries they point at, in the order the ids first appear.
 *
 * Routes through {@link computeCitationView} so the inline source chip groups sources by the exact
 * same URL/title rule (and drops `orderOfAppearance=0`/unknown ids the same way) as the "Källor (N)"
 * dropdown — two atoms citing the same help-center page collapse to a single entry on both surfaces
 * instead of each surface hand-rolling its own dedup and risking divergence.
 */
export function resolveCitedSources(refIds: string, metadata: ContentMetadata[] | undefined): CitationDisplayEntry[] {
    const { visibleEntries, displayOrderById } = computeCitationView(metadata);
    const seen = new Set<number>();
    const result: CitationDisplayEntry[] = [];
    for (const idStr of refIds.split(',')) {
        const displayOrder = displayOrderById.get(Number(idStr));
        if (displayOrder === undefined || seen.has(displayOrder)) {
            continue;
        }
        seen.add(displayOrder);
        // Every displayOrder was assigned from a pushed entry, so displayOrder - 1 (1-based → 0-based)
        // is always an in-range index into visibleEntries.
        result.push(visibleEntries[displayOrder - 1]);
    }
    return result;
}

function extractJsonFromString(streamedString: string): [string, Questions?] {
    const startPos = streamedString.indexOf('{');
    if (startPos === -1) {
        return [streamedString, undefined]; // No JSON part found
    }
    const endPos = streamedString.lastIndexOf('}');
    if (endPos === -1) {
        return [streamedString.substring(0, startPos), undefined];
    }

    const substring = streamedString.substring(startPos, endPos + 1);
    const openBracesCount = substring.split('{').length - 1;
    const closeBracesCount = substring.split('}').length - 1;

    const nonJsonPart = streamedString.substring(0, startPos);

    if (openBracesCount === closeBracesCount) {
        try {
            const questions = JSON.parse(substring) as Questions;
            return [nonJsonPart, questions];
        } catch (_) {
            return [streamedString, undefined]; // Return original string as non-JSON and empty string for JSON
        }
    } else {
        return [nonJsonPart, undefined]; // Could be a json string but it's not complete, so return part before possible JSON
    }
}

export function formatChatMessage(message: IChatMessage, useSourcesDropdown = false): string {
    let content = message.content;
    if (useSourcesDropdown && message.contentMetadataArray?.contentMetadata.length) {
        // New mode: group consecutive [[#N]] markers (possibly separated by spaces/tabs)
        // and replace with a single <sup> carrying all referenced content-metadata IDs so
        // the SourceChip component can render an interactive inline tile.
        // Only horizontal whitespace ([ \t]) is consumed — newlines are preserved so
        // markdown paragraph/list breaks aren't destroyed.
        content = content.replace(/\[\[#\d+\]\](?:[ \t]+\[\[#\d+\]\])*/g, (group) => {
            const ids = [...group.matchAll(/\[\[#(\d+)\]\]/g)].map((m) => m[1]);
            return `<sup data-ref-ids="${ids.join(',')}"></sup>`;
        });
    } else {
        // Legacy mode: numbered superscripts via computeCitationView dedup.
        // Built unconditionally — including when there is no contentMetadata at all
        // (undefined array, or an empty one for an answer built solely from internal admin
        // sources, #1702). The map is then empty, so any unmapped or hallucinated [[#n]]
        // collapses to an empty <sup> instead of rendering as literal "[[#1]]" text (#1706).
        const { displayOrderById } = computeCitationView(message.contentMetadataArray?.contentMetadata);
        const regex = /\[\[#(\d+)\]\]/g;
        const matches = content.match(regex);
        if (matches) {
            for (const match of matches) {
                const id = Number.parseInt(match.substring(3, match.length - 2));
                const displayOrder = displayOrderById.get(id);
                const link = `<sup>${displayOrder !== undefined ? displayOrder.toString() : ''}</sup>`;
                content = content.replace(match, link);
            }
        }
    }
    const [formattedContent] = extractJsonFromString(content);
    return formattedContent;
}
